import { Button, Divider, Flex, Text } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import CloseIcon from "@components/CloseIcon";
import UTMLink from "@components/UTMLink";
import { ETH } from "@config/contractsConfig";
import {
  toFixedWithoutRounding,
  SKIP_REWARDS_UNSTAKE_CTA,
  EARN_TOP_REWARDS_CTA,
} from "@constants/common";
import usePrices from "@hooks/usePrices";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";
import { LockKeyOpenIcon } from "@phosphor-icons/react";
import { useMemo, useCallback } from "react";
import { numberToCurrency } from "utils";
import { trackEvent } from "@lib/utils";
import { UNSTAKE_CARD_CTA_CLICKED } from "@constants/analytics";

// Constants
const PROJECTION_CONSTANTS = {
  START_AMOUNT: 10,
  PROJECTION_YEARS: 1,
  DEFAULT_APY: 0,
  DEFAULT_ETH_PRICE: 0,
} as const;

const VAULT_CONFIG = {
  TYPE: "hgETH" as const,
  NAME: "High Gain Vault",
  ICON: "/assets/vaults/hgeth.svg",
  LINK: "/gain/growth-vault/",
} as const;

// Types
interface UnstakeConfirmationProps {
  closeModal: () => void;
  action: () => void;
  eventName?: string;
}

interface ProjectedEarnings {
  startAmount: number;
  endAmount: number;
  profitUsd: number;
}

interface VaultData {
  apy: string;
  apyValue: number;
  tvl: string;
}

// Helper functions
const parseApyValue = (apyValue: unknown): number => {
  if (apyValue === null || apyValue === undefined) {
    return PROJECTION_CONSTANTS.DEFAULT_APY;
  }

  const numericValue = Number(apyValue);
  if (isNaN(numericValue) || !isFinite(numericValue)) {
    return PROJECTION_CONSTANTS.DEFAULT_APY;
  }

  return numericValue;
};

const getEthPrice = (
  assets: Record<string, { usd?: number } | null>
): number => {
  try {
    const ethAsset = assets[ETH];
    if (!ethAsset || typeof ethAsset !== "object") {
      return PROJECTION_CONSTANTS.DEFAULT_ETH_PRICE;
    }

    const price = ethAsset.usd;
    if (typeof price !== "number" || isNaN(price) || price <= 0) {
      return PROJECTION_CONSTANTS.DEFAULT_ETH_PRICE;
    }

    return price;
  } catch (error) {
    console.error("Error getting ETH price:", error);
    return PROJECTION_CONSTANTS.DEFAULT_ETH_PRICE;
  }
};

const calculateProjectedEarnings = (
  apyValue: number,
  ethPrice: number
): ProjectedEarnings => {
  const { START_AMOUNT, PROJECTION_YEARS } = PROJECTION_CONSTANTS;

  // Calculate end amount after projection period: startAmount * (1 + APY/100)^years
  const endAmount =
    START_AMOUNT * Math.pow(1 + apyValue / 100, PROJECTION_YEARS);

  // Calculate profit
  const profit = endAmount - START_AMOUNT;

  // Convert profit to USD (ETH price * profit in ETH)
  const profitUsd = Math.round(profit * ethPrice);

  return {
    startAmount: START_AMOUNT,
    endAmount: Number(endAmount.toFixed(2)),
    profitUsd,
  };
};

const UnstakeConfirmation = ({
  closeModal,
  action,
}: UnstakeConfirmationProps) => {
  // Fetch vault data with error handling
  const vaultDataResult = useVaultTvlApy(VAULT_CONFIG.TYPE);
  const pricesResult = usePrices();

  // Safely extract vault data with defaults
  const vaultData: VaultData = useMemo(() => {
    try {
      return {
        apy: vaultDataResult?.formattedApy ?? "-",
        apyValue: parseApyValue(vaultDataResult?.apy),
        tvl: vaultDataResult?.formattedTvl ?? "-",
      };
    } catch (error) {
      return {
        apy: "-",
        apyValue: PROJECTION_CONSTANTS.DEFAULT_APY,
        tvl: "-",
      };
    }
  }, [vaultDataResult]);

  // Safely extract ETH price with default
  const ethPrice = useMemo(() => {
    try {
      return getEthPrice(pricesResult?.assets ?? {});
    } catch (error) {
      console.error("Error getting prices:", error);
      return PROJECTION_CONSTANTS.DEFAULT_ETH_PRICE;
    }
  }, [pricesResult?.assets]);

  // Calculate projected earnings with memoization
  const projectedEarnings = useMemo(
    () => calculateProjectedEarnings(vaultData.apyValue, ethPrice),
    [vaultData.apyValue, ethPrice]
  );

  // Handlers
  const handleConfirmWithdraw = useCallback(() => {
    closeModal();
    action();
    trackEvent(UNSTAKE_CARD_CTA_CLICKED, {
      cta: SKIP_REWARDS_UNSTAKE_CTA,
    });
  }, [closeModal, action]);

  const handleKeepEarning = useCallback(() => {
    trackEvent(UNSTAKE_CARD_CTA_CLICKED, {
      cta: EARN_TOP_REWARDS_CTA,
    });
    // Navigation handled by UTMLink
  }, []);

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <CloseIcon onClose={closeModal} />
      {/* Header Icon */}
      <div className="flex items-center justify-center -mb-2 md:-mb-0">
        <div className="bg-[#F5B64C] rounded-[50%] h-[56px] w-[56px] md:h-[72px] md:w-[72px] flex items-center justify-center">
          <LockKeyOpenIcon size={36} color="#FFF" weight="fill" />
        </div>
      </div>

      {/* Header Text */}
      <div className="flex flex-col gap-1 items-center justify-center text-center">
        <Text className="text-[16px] sm:text-[20px] font-medium text-text-primary">
          Unstaking stops rewards
        </Text>
        <Text className="text-[12px] sm:text-[14px] font-normal text-[#4A6A6A] text-center max-w-[358px]">
          Your assets won&apos;t earn for 7-10 days. Consider switching to a
          vault to stay productive.
        </Text>
      </div>

      {/* Vault Information Card */}
      <div className="flex flex-col gap-5 p-4 sm:p-6 sm:pb-5 rounded-[12px] border-[1px] border-[#CCE5E5]">
        <UTMLink href={VAULT_CONFIG.LINK} onClick={() => closeModal()}>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:justify-between">
            <div className="flex items-center gap-1">
              <BaseImage
                src={VAULT_CONFIG.ICON}
                width={20}
                height={20}
                alt={VAULT_CONFIG.NAME}
              />
              <Text className="text-base font-normal text-[#1A1A1A]">
                {VAULT_CONFIG.NAME}
              </Text>

              <BaseImage
                src="/assets/arrow.svg"
                width={24}
                height={24}
                alt=""
                className="sm:hidden"
              />
            </div>

            <div className="flex items-center gap-4">
              <Text className="text-sm font-normal text-[#8A98A4]">
                APY: <span className="text-[#1A1A1A]">{vaultData.apy}</span>
              </Text>
              <Divider
                orientation="vertical"
                className="border-border-default border-[1px] h-5"
              />
              <Text className="text-sm font-normal text-[#8A98A4]">
                TVL: <span className="text-[#1A1A1A]">{vaultData.tvl}</span>
              </Text>
            </div>

            <BaseImage
              src="/assets/arrow.svg"
              width={24}
              height={24}
              alt=""
              className="hidden sm:block"
            />
          </div>
        </UTMLink>

        <Divider className="border-border-default w-full border-[1px]" />

        {/* Projected Earnings Section */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center px-2 py-1 mb-4 sm:mb-0 bg-[#70a1ff33] rounded-[100px]">
            <p className="text-xs font-normal text-[#1A1A1A]">
              Projected Earning (1 year)
            </p>
          </div>

          <div className="relative h-[72px]">
            <BaseImage
              src="/assets/increase_graph.png"
              width={208}
              height={64}
              alt="Projected earnings graph"
            />

            {/* Start amount - at the beginning of the graph */}
            <div className="absolute left-0 bottom-[10px]">
              <p className="text-[10px] font-normal text-[#1A1A1A]">
                {numberToCurrency(projectedEarnings.startAmount.toString(), 2)}{" "}
                ETH
              </p>
            </div>

            {/* Profit USD - in the middle of the image */}
            <div className="absolute left-1/2 -translate-x-1/2 top-1/3 -translate-y-1/2 bg-[#F4F4F7] p-[4px] rounded-[4px]">
              <p className="text-[10px] font-semibold text-[#28B58E]">
                +$
                {toFixedWithoutRounding(
                  projectedEarnings.profitUsd.toString(),
                  1
                )}
              </p>
            </div>

            {/* End amount - at the end of the graph */}
            <div className="absolute right-0 -top-[14px]">
              <p className="text-[10px] font-semibold text-[#28B58E]">
                ~
                {numberToCurrency(
                  toFixedWithoutRounding(
                    projectedEarnings.endAmount.toString(),
                    1
                  ),
                  1
                )}{" "}
                ETH
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <Flex
        width="100%"
        direction={{ base: "column", md: "row" }}
        gap={{ base: "12px", md: "12px" }}
      >
        <Button
          flex={{ base: "none", md: 1 }}
          backgroundColor="white"
          color="#075A5A"
          _hover={{
            backgroundColor: "white",
            color: "#075A5A",
          }}
          alignSelf="center"
          borderRadius="0.5rem"
          border="1px solid #EEEEEF"
          cursor="pointer"
          onClick={handleConfirmWithdraw}
          fontSize={{
            base: "14px",
            md: "18px",
          }}
          fontWeight={{
            base: "400",
            md: "500",
          }}
          width={{ base: "100%", md: "auto" }}
          height={{
            base: "40px",
          }}
          minHeight={{
            base: "40px",
          }}
        >
          {SKIP_REWARDS_UNSTAKE_CTA}
        </Button>
        <UTMLink
          href={VAULT_CONFIG.LINK}
          isExternal={true}
          flex={{ base: "none", md: 1 }}
          width={{ base: "100%", md: "auto" }}
          onClick={() => closeModal()}
          display="flex"
        >
          <Button
            width="100%"
            backgroundColor="#075A5A"
            color="white"
            _hover={{
              backgroundColor: "#075A5A",
              color: "white",
            }}
            alignSelf="center"
            borderRadius="0.5rem"
            cursor="pointer"
            onClick={handleKeepEarning}
            fontSize={{
              base: "14px",
              md: "18px",
            }}
            fontWeight={{
              base: "400",
              md: "500",
            }}
            height={{
              base: "40px",
            }}
            minHeight={{
              base: "40px",
            }}
          >
            {EARN_TOP_REWARDS_CTA}
          </Button>
        </UTMLink>
      </Flex>
    </div>
  );
};

export default UnstakeConfirmation;
